import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Theme } from "../.themes";

export type StickyPriceState = {
  price: string;
  prices?: { price: string }[];
  secondaryPrice?: {
    Amount?: string;
    Currency?: string;
    Period?: string;
  };
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: "flex",
    justifyContent: "center",
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    boxSizing: "border-box",
    padding: "24px",
    paddingLeft: "calc(24px + env(safe-area-inset-left))",
    paddingRight: "calc(24px + env(safe-area-inset-right))",
    paddingBottom: `calc(24px + env(safe-area-inset-bottom))`,
    margin: 0,
    zIndex: 1300,
    background: "rgba(255, 255, 255, 0.48)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
  },
  content: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    width: "100%",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    minWidth: 0,
  },
  label: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "inherit",
  },
  price: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    color: "inherit",
  },
  secondaryPrice: {
    marginLeft: "10px",
    fontWeight: 400,
  },
  secondaryPriceText: {
    fontSize: "14px",
  },
  secondaryPriceUnit: {
    fontSize: "12px",
  },
  button: {
    width: "100%",
    maxWidth: "384px",
    minHeight: "48px",
    borderRadius: "4px",
    border: `1px solid ${theme.stickyPriceButton?.borderColor}`,
    background: theme.stickyPriceButton?.backgroundColor,
    padding: "12px 24px",
    color: theme.stickyPriceButton?.textColor,
    cursor: "pointer",
    boxSizing: "border-box",
    "&:hover": {
      background: theme.stickyPriceButton?.hoverColor,
      color: theme.stickyPriceButton?.hoverTextColor ?? theme.stickyPriceButton?.textColor,
    },
    "&:focus-visible": {
      outline: "2px solid rgba(53, 85, 92, 0.4)",
      outlineOffset: "2px",
    },
  },
  spinner: {
    width: "16px",
    height: "16px",
    marginLeft: "8px",
    borderRadius: "50%",
    border: "2px solid currentColor",
    borderRightColor: "transparent",
    display: "inline-block",
    animation: "$spin 0.8s linear infinite",
  },
  "@keyframes spin": {
    to: {
      transform: "rotate(1turn)",
    },
  },
}));

export type StickyPriceButtonProps = {
  onClick: () => void;
  id: string;
  summaryPrice?: string;
  priceState?: StickyPriceState;
  isLoading?: boolean;
  labelText?: string;
  loadingText?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  showYearlyPrice?: boolean;
};

const StickyPriceButton: React.FC<StickyPriceButtonProps> = (props: StickyPriceButtonProps) => {
  const classes = useStyles();
  const [showSpinner, setShowSpinner] = useState(false);
  const active = props.isLoading ?? false;

  const prices = props.priceState?.prices;
  const lastPrice = prices && prices.length > 0 ? prices[prices.length - 1] : undefined;
  const summaryPrice = props.summaryPrice ?? lastPrice?.price ?? props.priceState?.price;

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setShowSpinner(active);
      },
      active ? 500 : 250,
    );
    return () => clearTimeout(timer);
  }, [active]);

  const renderYearlyPrice = () => {
    if (!props.showYearlyPrice) return null;
    if (!props.priceState?.secondaryPrice?.Amount) return null;
    return (
      <span className={classes.secondaryPrice}>
        (<span className={classes.secondaryPriceText}>{props.priceState.secondaryPrice.Amount} </span>
        <span className={classes.secondaryPriceUnit}>
          {props.priceState.secondaryPrice.Currency}/{props.priceState.secondaryPrice.Period}
        </span>
        )
      </span>
    );
  };

  return (
    <div className={clsx(classes.wrapper, props.wrapperClassName)}>
      <button
        type='button'
        id={props.id}
        className={clsx(classes.button, props.buttonClassName)}
        onClick={props.onClick}
        disabled={active}
        aria-busy={active ? "true" : undefined}
      >
        <span className={classes.content}>
          <span className={classes.label}>{showSpinner ? props.loadingText ?? "Uppdaterar pris" : props.labelText ?? "Fortsätt"}</span>
          {summaryPrice && !showSpinner && (
            <span className={classes.price}>
              {summaryPrice}

              {renderYearlyPrice()}
            </span>
          )}
          {showSpinner && <span className={classes.spinner} aria-hidden='true' />}
        </span>
      </button>
    </div>
  );
};

export default StickyPriceButton;
